import React, { useState, useEffect, useRef } from 'react';
import './TimePickerModal.css';

type Timeframe = 'day' | 'week' | 'month' | 'year';

interface TimePickerModalProps {
    isOpen: boolean;
    onClose: () => void;
    timeframe: Timeframe;
    onSelect: (date: Date) => void;
    selectedDate: Date;
}

const TimePickerModal: React.FC<TimePickerModalProps> = ({
    isOpen,
    onClose,
    timeframe,
    onSelect,
    selectedDate,
}) => {
    const [currentDate, setCurrentDate] = useState(selectedDate);
    const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
    const [weekDates, setWeekDates] = useState<Date[]>([]);
    const [hoveredMonth, setHoveredMonth] = useState<number | null>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const yearListRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setCurrentDate(selectedDate);
    }, [selectedDate]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    useEffect(() => {
        if (isOpen && timeframe === 'week') {
            setWeekDates(getWeekDates(selectedDate));
        }
    }, [isOpen, timeframe, selectedDate]);

    const getDaysInMonth = (date: Date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        return new Date(year, month + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date: Date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        return new Date(year, month, 1).getDay();
    };

    const isFutureDate = (date: Date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date > today;
    };

    const getWeekDates = (startDate: Date): Date[] => {
        const dates: Date[] = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // If we can't get 6 days after, get some days before
        let daysAfter = 0;
        let daysBefore = 0;
        const testDate = new Date(startDate);

        while (daysAfter < 6) {
            testDate.setDate(testDate.getDate() + 1);
            if (testDate > today) {
                break;
            }
            daysAfter++;
        }

        if (daysAfter < 6) {
            daysBefore = 6 - daysAfter;
        }

        // Add days before
        for (let i = daysBefore; i > 0; i--) {
            const date = new Date(startDate);
            date.setDate(date.getDate() - i);
            dates.push(date);
        }

        // Add the start date
        dates.push(new Date(startDate));

        // Add days after
        for (let i = 1; i <= daysAfter; i++) {
            const date = new Date(startDate);
            date.setDate(date.getDate() + i);
            dates.push(date);
        }

        return dates;
    };

    const handleDateClick = (date: Date) => {
        if (isFutureDate(date)) return;
        onSelect(date);
    };

    const handleMonthChange = (increment: number) => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(prev.getMonth() + increment);
            return newDate;
        });
    };

    const handleYearChange = (increment: number) => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setFullYear(prev.getFullYear() + increment);
            return newDate;
        });
    };

    // Add new useEffect for year scrolling
    useEffect(() => {
        if (isOpen && timeframe === 'year' && yearListRef.current) {
            // Add a small delay to allow elements to render
            const timer = setTimeout(() => {
                if (yearListRef.current) {
                    const currentYearElement = yearListRef.current.querySelector('.picker-current-year');
                    if (currentYearElement) {
                        currentYearElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }
            }, 100);
            return () => clearTimeout(timer); // Cleanup timeout
        }
    }, [isOpen, timeframe, selectedDate]);

    // Effect for fading years based on scroll position
    useEffect(() => {
        const yearListElement = yearListRef.current;

        if (!isOpen || timeframe !== 'year' || !yearListElement) {
            return; // Only apply if modal is open, in year view, and ref is available
        }

        const updateYearOpacity = () => {
            const htmlYearListElement = yearListElement as HTMLElement;
            const containerHeight = htmlYearListElement.offsetHeight;
            const scrollTop = htmlYearListElement.scrollTop;
            const scrollBottom = scrollTop + containerHeight;

            const yearElements = yearListElement.querySelectorAll('.picker-year-cell');

            yearElements.forEach(yearElement => {
                const htmlYearElement = yearElement as HTMLElement;
                const elementRect = htmlYearElement.getBoundingClientRect();
                const htmlYearListElementRect = htmlYearListElement.getBoundingClientRect();
                const elementTop = elementRect.top + scrollTop - htmlYearListElementRect.top;
                const elementHeight = htmlYearElement.offsetHeight;
                const elementCenter = elementTop + elementHeight / 2;

                // Calculate a value based on distance from the center of the scroll view
                // This is a basic example, the function might need tuning
                const distanceFromCenter = Math.abs(elementCenter - (scrollTop + containerHeight / 2));
                const maxDistance = containerHeight / 2;
                const opacity = 1 - Math.min(1, distanceFromCenter / maxDistance);

                // Apply opacity, ensure minimum opacity if desired
                htmlYearElement.style.opacity = Math.max(0.1, opacity).toString(); // Example: min opacity 0.1
            });
        };

        // Initial update and add scroll listener
        updateYearOpacity();
        yearListElement.addEventListener('scroll', updateYearOpacity);

        // Cleanup scroll listener
        return () => {
            yearListElement.removeEventListener('scroll', updateYearOpacity);
        };

    }, [isOpen, timeframe]); // Re-run effect if modal open state or timeframe changes

    const renderDayView = () => {
        const daysInMonth = getDaysInMonth(currentDate);
        const firstDay = getFirstDayOfMonth(currentDate);
        const days = [];

        // Add empty cells for days before the first day of the month
        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="picker-empty-cell" />);
        }

        // Add cells for each day of the month
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
            const isSelected = selectedDate?.toDateString() === date.toDateString();
            const isHovered = hoveredDate?.toDateString() === date.toDateString();
            const isFuture = isFutureDate(date);

            days.push(
                <div
                    key={`day-${day}`}
                    className={`picker-day-cell ${isSelected ? 'picker-selected' : ''} ${isHovered ? 'picker-hovered' : ''} ${isFuture ? 'picker-future' : ''} ${isSelected && isHovered ? 'picker-selected-hovered' : ''}`}
                    onClick={() => handleDateClick(date)}
                    onMouseEnter={() => !isFuture && setHoveredDate(date)}
                    onMouseLeave={() => setHoveredDate(null)}
                >
                    {day}
                </div>
            );
        }

        const currentRealDate = new Date();
        currentRealDate.setHours(0, 0, 0, 0);
        const canAdvance = (currentDate.getFullYear() < currentRealDate.getFullYear()) ||
            (currentDate.getFullYear() === currentRealDate.getFullYear() && currentDate.getMonth() < currentRealDate.getMonth());

        return (
            <div className="picker-day-view">
                <div className="picker-header">
                    <button onClick={() => handleMonthChange(-1)}>
                        <img src="/assets/dashboard/ArrowLeft.svg" alt="Previous month" />
                    </button>
                    <span>{currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
                    <button
                        onClick={() => { if (canAdvance) handleMonthChange(1); }}
                        disabled={!canAdvance}
                        style={{ opacity: canAdvance ? 1 : 0.3 }}
                    >
                        <img src="/assets/dashboard/ArrowRight.svg" alt="Next month" />
                    </button>
                </div>
                <div className="picker-weekdays">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="picker-weekday">{day}</div>
                    ))}
                </div>
                <div className="picker-days-grid">
                    {days}
                </div>
            </div>
        );
    };

    const renderWeekView = () => {
        const daysInMonth = getDaysInMonth(currentDate);
        const firstDay = getFirstDayOfMonth(currentDate);
        const days = [];

        // Add empty cells for days before the first day of the month
        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="picker-empty-cell" />);
        }

        // Add cells for each day of the month
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
            const isSelected = selectedDate?.toDateString() === date.toDateString();
            const isHovered = weekDates.some(d => d.toDateString() === date.toDateString());
            const isFuture = isFutureDate(date);

            days.push(
                <div
                    key={`day-${day}`}
                    className={`picker-day-cell ${isSelected ? 'picker-selected' : (isHovered ? 'picker-hovered' : '')} ${isFuture ? 'picker-future' : ''}`}
                    onClick={() => handleDateClick(date)}
                    onMouseEnter={() => !isFuture && setWeekDates(getWeekDates(date))}
                >
                    {day}
                </div>
            );
        }

        const currentRealDate = new Date();
        currentRealDate.setHours(0, 0, 0, 0);
        const canAdvance = (currentDate.getFullYear() < currentRealDate.getFullYear()) ||
            (currentDate.getFullYear() === currentRealDate.getFullYear() && currentDate.getMonth() < currentRealDate.getMonth());

        return (
            <div className="picker-day-view" onMouseLeave={() => setWeekDates(getWeekDates(selectedDate))}>
                <div className="picker-header">
                    <button onClick={() => handleMonthChange(-1)}>
                        <img src="/assets/dashboard/ArrowLeft.svg" alt="Previous month" />
                    </button>
                    <span>{currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
                    <button
                        onClick={() => { if (canAdvance) handleMonthChange(1); }}
                        disabled={!canAdvance}
                        style={{ opacity: canAdvance ? 1 : 0.3 }}
                    >
                        <img src="/assets/dashboard/ArrowRight.svg" alt="Next month" />
                    </button>
                </div>
                <div className="picker-weekdays">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="picker-weekday">{day}</div>
                    ))}
                </div>
                <div className="picker-days-grid">
                    {days}
                </div>
            </div>
        );
    };

    const renderMonthView = () => {
        const todayMidnight = new Date();
        todayMidnight.setHours(0, 0, 0, 0);
        const months = Array.from({ length: 12 }, (_, i) => {
            const monthDate = new Date(currentDate.getFullYear(), i, 1);
            const isFuture = monthDate > todayMidnight;
            const isSelected = selectedDate.getMonth() === i && selectedDate.getFullYear() === currentDate.getFullYear();
            const cellClass = isFuture ? 'picker-future' : (isSelected ? (hoveredMonth === i ? 'picker-selected-hovered' : 'picker-selected') : (hoveredMonth === i ? 'picker-hovered' : ''));
            return (
                <div
                    key={`month-${i}`}
                    className={`picker-month-cell ${cellClass}`}
                    onClick={() => handleDateClick(monthDate)}
                    onMouseEnter={() => setHoveredMonth(i)}
                    onMouseLeave={() => setHoveredMonth(null)}
                >
                    {monthDate.toLocaleString('default', { month: 'short' })}
                </div>
            );
        });

        const currentRealYear = new Date().getFullYear();

        return (
            <div className="picker-month-view">
                <div className="picker-header">
                    <button onClick={() => handleYearChange(-1)}>
                        <img src="/assets/dashboard/ArrowLeft.svg" alt="Previous year" />
                    </button>
                    <span>{currentDate.getFullYear()}</span>
                    <button
                        onClick={() => { if (currentDate.getFullYear() < currentRealYear) { handleYearChange(1); } }}
                        disabled={currentDate.getFullYear() >= currentRealYear}
                        style={{ opacity: currentDate.getFullYear() >= currentRealYear ? 0.3 : 1 }}
                    >
                        <img src="/assets/dashboard/ArrowRight.svg" alt="Next year" />
                    </button>
                </div>
                <div className="picker-months-grid">
                    {months}
                </div>
            </div>
        );
    };

    const renderYearView = () => {
        const actualCurrentYear = new Date().getFullYear();
        const startYear = 2000;
        const endYear = actualCurrentYear + 3;
        const years = [];
        // Generate years from actualCurrentYear + 3 down to 2000
        for (let year = endYear; year >= startYear; year--) {
            const date = new Date(year, 0, 1);
            const isSelected = selectedDate.getFullYear() === year;
            // Condition for fading/non-clickable based on new requirements
            const isFadedAndNotClickable = year < 2020 || year > actualCurrentYear;
            years.push(
                <div
                    key={`year-${year}`}
                    className={`picker-year-cell ${year === selectedDate.getFullYear() ? 'picker-current-year' : ''} ${isSelected ? 'picker-selected' : ''} ${isFadedAndNotClickable ? 'picker-future' : ''}`}
                    onClick={() => !isFadedAndNotClickable && handleDateClick(date)}
                >
                    {year}
                </div>
            );
        }

        return (
            <div className="picker-year-view" ref={yearListRef}>
                <div className="picker-years-list">
                    {years}
                </div>
            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="picker-modal" ref={modalRef}>
                <div className="modal-close" onClick={onClose}>
                    <span className="modal-cancel-text">Cancel (ESC)</span>
                    <img src="/assets/x.svg" alt="Close" className="modal-close-icon" />
                </div>
                {timeframe === 'day' && renderDayView()}
                {timeframe === 'week' && renderWeekView()}
                {timeframe === 'month' && renderMonthView()}
                {timeframe === 'year' && renderYearView()}
            </div>
        </div>
    );
};

export default TimePickerModal; 